<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-bdc-black mb-2">Dashboard Administrateur</h1>
        <p class="text-gray-600">Gestion et monitoring de la plateforme Bruits de Couloir</p>
      </div>

      <!-- Stats rapides -->
      <div class="grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
        <div
          class="bg-white p-2 sm:p-4 lg:p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
        >
          <div
            class="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left"
          >
            <div
              class="p-1.5 sm:p-2 lg:p-3 rounded-full bg-bdc-ocre/10 text-bdc-ocre mb-1 sm:mb-0 sm:mr-2 lg:mr-4 flex-shrink-0"
            >
              <FileText class="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600 truncate">
                Graffitis
              </p>
              <p class="text-sm sm:text-lg lg:text-2xl font-semibold text-bdc-black truncate">
                {{ stats.graffitis }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="bg-white p-2 sm:p-4 lg:p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
        >
          <div
            class="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left"
          >
            <div
              class="p-1.5 sm:p-2 lg:p-3 rounded-full bg-bdc-terracotta/10 text-bdc-terracotta mb-1 sm:mb-0 sm:mr-2 lg:mr-4 flex-shrink-0"
            >
              <Tag class="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600 truncate">
                Thématiques
              </p>
              <p class="text-sm sm:text-lg lg:text-2xl font-semibold text-bdc-black truncate">
                {{ stats.thematiques }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="bg-white p-2 sm:p-4 lg:p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
        >
          <div
            class="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left"
          >
            <div
              class="p-1.5 sm:p-2 lg:p-3 rounded-full bg-blue-100 text-blue-600 mb-1 sm:mb-0 sm:mr-2 lg:mr-4 flex-shrink-0"
            >
              <Link class="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600 truncate">
                Liens
              </p>
              <p class="text-sm sm:text-lg lg:text-2xl font-semibold text-bdc-black truncate">
                {{ stats.liens }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="bg-white p-2 sm:p-4 lg:p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
        >
          <div
            class="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left"
          >
            <div
              class="p-1.5 sm:p-2 lg:p-3 rounded-full bg-red-100 text-red-600 mb-1 sm:mb-0 sm:mr-2 lg:mr-4 flex-shrink-0"
            >
              <Bug class="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600 truncate">
                Bugs
              </p>
              <p class="text-sm sm:text-lg lg:text-2xl font-semibold text-bdc-black truncate">
                {{ stats.bugsOpen }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="bg-white p-2 sm:p-4 lg:p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow col-span-4 sm:col-span-1"
        >
          <div
            class="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left"
          >
            <div
              class="p-1.5 sm:p-2 lg:p-3 rounded-full mb-1 sm:mb-0 sm:mr-2 lg:mr-4 flex-shrink-0"
              :class="
                systemStatus.healthy ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
              "
            >
              <CheckCircle
                v-if="systemStatus.healthy"
                class="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6"
              />
              <AlertCircle v-else class="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600 truncate">
                Système
              </p>
              <p
                class="text-[10px] sm:text-xs lg:text-sm font-semibold truncate"
                :class="systemStatus.healthy ? 'text-green-600' : 'text-red-600'"
              >
                {{ systemStatus.healthy ? 'OK' : 'Erreur' }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Dashboard Cards -->
      <div class="space-y-10">
        <!-- Gestion -->
        <section>
          <h2 class="text-xl font-semibold text-bdc-black mb-4 pb-2 border-b border-gray-200">
            Gestion
          </h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <!-- Gestion des utilisateurs -->
            <DashboardCard
              title="Gestion des Utilisateurs"
              description="Gérer les comptes et les rôles des utilisateurs"
              :icon="Users"
              to="/admin/users"
              icon-color="text-bdc-ocre"
              icon-bg-color="bg-bdc-ocre/10"
            />

            <!-- Gestion des traductions -->
            <DashboardCard
              title="Gestion des Traductions"
              description="Éditer les traductions de l'interface"
              :icon="Languages"
              to="/admin/translations"
              icon-color="text-bdc-ocre"
              icon-bg-color="bg-bdc-ocre/10"
            />

            <!-- Gestion des Bugs -->
            <DashboardCard
              title="Gestion des Bugs"
              description="Consulter et gérer les remontées de bugs"
              :icon="Bug"
              to="/admin/bug-reports"
              icon-color="text-bdc-ocre"
              icon-bg-color="bg-bdc-ocre/10"
            />

            <!-- Gestion des Actualités -->
            <DashboardCard
              title="Gestion des Actualités"
              description="Créer et gérer les articles de blog/actualités"
              :icon="Newspaper"
              to="/researcher/blog"
              icon-color="text-bdc-ocre"
              icon-bg-color="bg-bdc-ocre/10"
            />
          </div>
        </section>

        <!-- Développement & Tests -->
        <section>
          <h2 class="text-xl font-semibold text-bdc-black mb-4 pb-2 border-b border-gray-200">
            Développement & Tests
          </h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <!-- Test API -->
            <DashboardCard
              title="Test API"
              description="Tester les connexions et endpoints de l'API"
              :icon="Wrench"
              to="/dev/api-test"
              icon-color="text-bdc-terracotta"
              icon-bg-color="bg-bdc-terracotta/10"
            />
          </div>
        </section>

        <!-- Administration des données -->
        <section>
          <h2 class="text-xl font-semibold text-bdc-black mb-4 pb-2 border-b border-gray-200">
            Administration des données
          </h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <!-- Sauvegarde/Restauration -->
            <DashboardCard
              :title="$t('researcher.dashboard.cards.dataBackup.title')"
              :description="$t('researcher.dashboard.cards.dataBackup.description')"
              :icon="Database"
              to="/admin/data-backup"
              icon-color="text-bdc-black"
              icon-bg-color="bg-bdc-black/10"
            />
          </div>
        </section>

        <!-- Configuration système -->
        <section>
          <h2 class="text-xl font-semibold text-bdc-black mb-4 pb-2 border-b border-gray-200">
            Configuration système
          </h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <!-- Permissions -->
            <DashboardCard
              title="Permissions"
              description="Configurer les permissions par rôle"
              :icon="Lock"
              disabled
              icon-color="text-bdc-ocre"
              icon-bg-color="bg-bdc-ocre/10"
            />

            <!-- Paramètres Système -->
            <DashboardCard
              title="Paramètres Système"
              description="Configuration générale de l'application"
              :icon="Settings"
              disabled
              icon-color="text-bdc-ocre"
              icon-bg-color="bg-bdc-ocre/10"
            />

            <!-- API & Intégrations -->
            <DashboardCard
              title="API & Intégrations"
              description="Configurer les connexions externes"
              :icon="Plug"
              disabled
              icon-color="text-bdc-ocre"
              icon-bg-color="bg-bdc-ocre/10"
            />

            <!-- Cache & Performance -->
            <DashboardCard
              title="Cache & Performance"
              description="Gestion du cache et optimisations"
              :icon="Rocket"
              disabled
              icon-color="text-bdc-ocre"
              icon-bg-color="bg-bdc-ocre/10"
            />
          </div>
        </section>

        <!-- Monitoring -->
        <section>
          <h2 class="text-xl font-semibold text-bdc-black mb-4 pb-2 border-b border-gray-200">
            Monitoring
          </h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <!-- Activité -->
            <DashboardCard
              title="Activité Utilisateurs"
              description="Voir l'activité des utilisateurs"
              :icon="TrendingUp"
              disabled
              icon-color="text-bdc-terracotta"
              icon-bg-color="bg-bdc-terracotta/10"
            />

            <!-- Logs Système -->
            <DashboardCard
              title="Logs Système"
              description="Visualiser les logs du backend et frontend"
              :icon="FileText"
              disabled
              icon-color="text-bdc-terracotta"
              icon-bg-color="bg-bdc-terracotta/10"
            />

            <!-- Performance -->
            <DashboardCard
              title="Performance"
              description="Monitoring des performances de l'application"
              :icon="Zap"
              disabled
              icon-color="text-bdc-terracotta"
              icon-bg-color="bg-bdc-terracotta/10"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from '@/api'
import DashboardCard from '@/components/UI/DashboardCard.vue'
import { useBugReportStore } from '@/stores/bugReport'
import {
  AlertCircle,
  Bug,
  CheckCircle,
  Database,
  FileText,
  Languages,
  Link,
  Lock,
  Newspaper,
  Plug,
  Rocket,
  Settings,
  Tag,
  TrendingUp,
  Users,
  Wrench,
  Zap,
} from 'lucide-vue-next'
import { onMounted, ref } from 'vue'

console.log('[BdC-AdminDashboard] Chargement du dashboard administrateur')

const bugReportStore = useBugReportStore()

// État
const stats = ref({
  graffitis: '...',
  thematiques: '...',
  liens: '...',
  bugsOpen: 0,
})

const systemStatus = ref({
  healthy: true,
})

// Charger les statistiques
onMounted(async () => {
  console.log('[BdC-AdminDashboard] Dashboard administrateur monté avec succès')
  await loadStats()
  await loadBugStats()
  await checkSystemHealth()
})

async function loadStats() {
  try {
    console.log('[BdC-AdminDashboard] Chargement des statistiques')

    // Charger toutes les stats via l'API system
    const systemStats = await api.system.getStats()

    if (systemStats.success && systemStats.data) {
      stats.value.graffitis = systemStats.data.totalGraffitis.toLocaleString()
      stats.value.thematiques = systemStats.data.totalThematiques.toLocaleString()
      stats.value.liens = systemStats.data.totalLiens?.toLocaleString() || '0'
    } else {
      // Fallback sur les anciennes méthodes si l'API system ne fonctionne pas
      const graffitisResponse = await api.graffitis.list({ limit: 1 })
      stats.value.graffitis = graffitisResponse.pagination?.total?.toLocaleString() || '0'

      const thematiques = await api.thematiques.getAll()
      stats.value.thematiques = thematiques.length.toString()
      stats.value.liens = 'N/A'
    }

    console.log('[BdC-AdminDashboard] Stats chargées:', stats.value)
  } catch (error) {
    console.error('[BdC-AdminDashboard] Erreur chargement stats:', error)
    stats.value = { graffitis: 'Erreur', thematiques: 'Erreur', liens: 'Erreur', bugsOpen: 0 }
  }
}

async function loadBugStats() {
  try {
    console.log('[BdC-AdminDashboard] Chargement statistiques bugs')
    await bugReportStore.loadStats()
    stats.value.bugsOpen = bugReportStore.stats?.open || 0
    console.log('[BdC-AdminDashboard] Stats bugs chargées:', bugReportStore.stats)
  } catch (error) {
    console.error('[BdC-AdminDashboard] Erreur chargement stats bugs:', error)
  }
}

async function checkSystemHealth() {
  try {
    await api.system.healthCheck()
    systemStatus.value.healthy = true
    console.log('[BdC-AdminDashboard] Système opérationnel')
  } catch (error) {
    systemStatus.value.healthy = false
    console.error('[BdC-AdminDashboard] Problème système détecté:', error)
  }
}
</script>
